import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { finalize } from 'rxjs/operators';
import { ToastPanelType } from 'src/app/config/app-constants';
import { GenericConfirmationDialogComponent } from 'src/app/shared/generic-confirmation-dialog/generic-confirmation-dialog.component';
import { ToastService } from 'src/app/shared/services/toast.service';
import { DeploymentAction, SharedVolume, SharedVolumeConsumer } from '../../models/device.model';
import { FleetManagementService } from '../../services/fleet-management.service';

export interface CertManagerDialogData {
  edgeDeviceId: string;
  action: DeploymentAction;
}

interface CertManagerEnvelopeData {
  operation?: string;
  moduleId?: string;
  status?: number;
  body?: string;
}

interface CertManagerEnvelope {
  success: boolean;
  message?: string;
  data?: CertManagerEnvelopeData;
}

interface CertManagerModuleResponse {
  success?: boolean;
  message?: string;
  errorCode?: string;
  certificates?: CertificateRecord[];
}

interface CertificateRecord {
  name?: string;
  relativePath?: string;
  fullPath?: string;
  createdAtUtc?: string;
  lastModifiedAtUtc?: string;
  expiresAtUtc?: string;
}

interface ResolvedConsumerPath {
  moduleType: string;
  friendlyName: string;
  path: string;
}

@Component({
  selector: 'app-cert-manager-dialog',
  templateUrl: './cert-manager-dialog.component.html',
  styleUrls: ['./cert-manager-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ClipboardModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatTableModule
  ]
})
export class CertManagerDialogComponent implements OnInit {
  readonly listOperationName = 'listcert';
  readonly installOperationName = 'installcert';
  readonly removeOperationName = 'removecert';

  readonly certificateTypes: Array<'PEM' | 'PFX' | 'CER' | 'DER'> = ['PEM', 'PFX', 'CER', 'DER'];
  readonly acceptedCertificateFileTypes = '.pem,.key,.crt,.cer,.der,.pfx,.p12';
  readonly certificateNamePattern = /^[A-Za-z0-9./-]+$/;

  certificates: CertificateRecord[] = [];
  /** Resolved consumer paths per certificate, keyed by certificate name. */
  consumerPathsMap: Map<string, ResolvedConsumerPath[]> = new Map();
  /** Whether the action exposes any non-cert-manager shared volume consumers. */
  hasConsumers = false;
  selectedFile: File | null = null;
  certificateName = '';
  certificateType: 'PEM' | 'PFX' | 'CER' | 'DER' = 'PEM';
  pfxPassword = '';

  loadingList = false;
  installing = false;
  removingPath: string | null = null;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CertManagerDialogData,
    public dialogRef: MatDialogRef<CertManagerDialogComponent>,
    private readonly dialog: MatDialog,
    private readonly fleetManagementService: FleetManagementService,
    private readonly toastService: ToastService,
    private readonly ngxLoader: NgxUiLoaderService
  ) { }

  ngOnInit(): void {
    console.debug('[CertManagerDialog] Initialized', {
      edgeDeviceId: this.data?.edgeDeviceId,
      action: this.data?.action
    });
    this.hasConsumers = this.getAllConsumers().length > 0;
    this.refreshCertificates();
  }

  refreshCertificates(): void {
    console.debug('[CertManagerDialog] Refresh certificates requested', {
      edgeDeviceId: this.data?.edgeDeviceId
    });
    this.loadingList = true;
    const loaderKey = `CertManagerList_${this.data.edgeDeviceId}`;
    this.ngxLoader.start(loaderKey);

    this.executeOperation(this.listOperationName, {})
      .pipe(finalize(() => {
        this.loadingList = false;
        this.ngxLoader.stop(loaderKey);
      }))
      .subscribe({
        next: (res) => {
          console.debug('[CertManagerDialog] listcert response envelope', res);
          const parsed = this.parseModuleBody(res?.data?.body);
          if (!res?.success || !parsed?.success) {
            this.certificates = [];
            this.toastService.openToast(parsed?.message || res?.message || 'Unable to list certificates', ToastPanelType.error);
            return;
          }

          this.certificates = Array.isArray(parsed.certificates) ? parsed.certificates : [];
          this.buildConsumerPathsMap();
        },
        error: () => {
          console.error('[CertManagerDialog] listcert request failed');
          this.certificates = [];
          this.toastService.openToast('Unable to list certificates', ToastPanelType.error);
        }
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.length ? input.files[0] : null;

    this.selectedFile = file;
    if (!file) {
      return;
    }

    this.certificateType = this.inferCertificateType(file.name);

    const dotIndex = file.name.lastIndexOf('.');
    this.certificateName = dotIndex > 0 ? file.name.substring(0, dotIndex) : file.name;
  }

  uploadCertificate(): void {
    if (!this.selectedFile) {
      this.toastService.openToast('Please choose a certificate file', ToastPanelType.warning);
      return;
    }

    if (!this.certificateName?.trim()) {
      this.toastService.openToast('Certificate name is required', ToastPanelType.warning);
      return;
    }

    if (!this.isValidCertificateName(this.certificateName.trim())) {
      this.toastService.openToast('Invalid characters in certificate name. You can use A-Z, a-z, 0-9, -,., and /.', ToastPanelType.warning);
      return;
    }

    this.installing = true;
    const loaderKey = `CertManagerInstall_${this.data.edgeDeviceId}`;
    this.ngxLoader.start(loaderKey);

    this.fileToBase64(this.selectedFile)
      .then(certificateData => {
        if (!this.isValidBase64(certificateData)) {
          throw new Error('certificateData must be valid base64');
        }

        const args: any = {
          certificateName: this.certificateName.trim(),
          certificateData,
          certificateType: this.certificateType
        };

        if (this.certificateType === 'PFX' && this.pfxPassword) {
          args.password = this.pfxPassword;
        }

        this.executeOperation(this.installOperationName, args)
          .pipe(finalize(() => {
            this.installing = false;
            this.ngxLoader.stop(loaderKey);
          }))
          .subscribe({
            next: (res) => {
              const parsed = this.parseModuleBody(res?.data?.body);
              if (!res?.success || !parsed?.success) {
                this.toastService.openToast(parsed?.message || res?.message || 'Unable to upload certificate', ToastPanelType.error);
                return;
              }

              this.toastService.openToast(parsed?.message || 'Certificate uploaded', ToastPanelType.done);
              this.pfxPassword = '';
              this.refreshCertificates();
            },
            error: () => {
              this.toastService.openToast('Unable to upload certificate', ToastPanelType.error);
            }
          });
      })
      .catch(() => {
        this.installing = false;
        this.ngxLoader.stop(loaderKey);
        this.toastService.openToast('Unable to read selected file', ToastPanelType.error);
      });
  }

  removeCertificate(cert: CertificateRecord): void {
    const certificatePath = cert?.relativePath || cert?.name;
    if (!certificatePath) {
      this.toastService.openToast('Certificate path is missing', ToastPanelType.error);
      return;
    }

    const certificateName = cert?.name || certificatePath;
    const escapedCertificateName = this.escapeHtml(certificateName);
    const message = `Are you sure you want to remove ${certificateName}?\n\nPlease make sure it is not used in a CIS Channel.`;
    const messageHtml = `Are you sure you want to remove <strong>${escapedCertificateName}</strong>?<br><br>Please make sure it is not used in a CIS Channel.`;

    const confirmDialogRef = this.dialog.open(GenericConfirmationDialogComponent, {
      backdropClass: 'smDialog'
    });

    const instance = confirmDialogRef.componentInstance;
    instance.title = 'Remove Certificate';
    instance.message = message;
    instance.messageHtml = messageHtml;
    instance.dialogRef = confirmDialogRef;

    confirmDialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) {
        return;
      }

      this.removingPath = certificatePath;
      const loaderKey = `CertManagerRemove_${this.data.edgeDeviceId}`;
      this.ngxLoader.start(loaderKey);

      this.executeOperation(this.removeOperationName, { certificatePath })
        .pipe(finalize(() => {
          this.removingPath = null;
          this.ngxLoader.stop(loaderKey);
        }))
        .subscribe({
          next: (res) => {
            const parsed = this.parseModuleBody(res?.data?.body);
            if (!res?.success || !parsed?.success) {
              const removeError = parsed?.errorCode
                ? `${parsed.errorCode}: ${parsed.message || 'Unable to remove certificate'}`
                : (parsed?.message || res?.message || 'Unable to remove certificate');
              this.toastService.openToast(removeError, ToastPanelType.error);
              return;
            }

            this.toastService.openToast(parsed?.message || 'Certificate removed', ToastPanelType.done);
            this.refreshCertificates();
          },
          error: () => {
            this.toastService.openToast('Unable to remove certificate', ToastPanelType.error);
          }
        });
    });
  }

  onPathCopied(path: string): void {
    if (!path) {
      return;
    }

    this.toastService.openToast('Certificate path copied', ToastPanelType.info, 2);
  }

  /** Get the table columns to display. */
  get displayedColumns(): string[] {
    return ['name', 'expiresAtUtc', 'actions'];
  }

  /** Returns non-cert-manager consumers for the relevant shared volumes. */
  private getAllConsumers(): SharedVolumeConsumer[] {
    const action = this.data?.action;
    const sharedVolumes: SharedVolume[] = action?.sharedVolumes ?? [];
    const relevantVolumeNames: string[] = action?.ui?.metadata?.sharedVolumes ?? sharedVolumes.map(v => v.name);

    const consumers: SharedVolumeConsumer[] = [];
    for (const volName of relevantVolumeNames) {
      const vol = sharedVolumes.find(v => v.name === volName);
      if (!vol) continue;
      for (const consumer of vol.consumers) {
        if (consumer.moduleType === 'CertManagerModule') continue;
        consumers.push(consumer);
      }
    }
    return consumers;
  }

  /** Build the map of resolved consumer paths for all certificates. */
  private buildConsumerPathsMap(): void {
    this.consumerPathsMap = new Map();
    const consumers = this.getAllConsumers();
    if (!consumers.length) return;

    for (const cert of this.certificates) {
      if (!cert.relativePath) continue;
      const paths: ResolvedConsumerPath[] = consumers.map(c => ({
        moduleType: c.moduleType,
        friendlyName: c.friendlyName ?? c.moduleType,
        path: `${c.containerPath}/${cert.relativePath}`
      }));
      this.consumerPathsMap.set(cert.relativePath, paths);
    }
  }

  private isValidCertificateName(certificateName: string): boolean {
    return this.certificateNamePattern.test(certificateName);
  }

  private executeOperation(operation: string, argumentsBody: any) {
    console.debug('[CertManagerDialog] executeOperation', {
      edgeDeviceId: this.data?.edgeDeviceId,
      actionName: this.data?.action?.name || 'ManageCertificates',
      operation,
      argumentKeys: Object.keys(argumentsBody ?? {})
    });

    return this.fleetManagementService.executeDeploymentActionOperation(
      this.data.edgeDeviceId,
      this.data?.action?.name || 'ManageCertificates',
      operation,
      { Arguments: argumentsBody ?? {} }
    );
  }

  private parseModuleBody(rawBody?: string): CertManagerModuleResponse {
    if (!rawBody) {
      return {};
    }

    try {
      return JSON.parse(rawBody);
    } catch {
      return {};
    }
  }

  private inferCertificateType(fileName: string): 'PEM' | 'PFX' | 'CER' | 'DER' {
    const lower = (fileName || '').toLowerCase();
    if (lower.endsWith('.pfx') || lower.endsWith('.p12')) {
      return 'PFX';
    }
    if (lower.endsWith('.cer')) {
      return 'CER';
    }
    if (lower.endsWith('.der')) {
      return 'DER';
    }

    return 'PEM';
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const result = reader.result;
        if (!(result instanceof ArrayBuffer)) {
          reject(new Error('Unable to read certificate bytes'));
          return;
        }

        const bytes = new Uint8Array(result);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i += 1) {
          binary += String.fromCharCode(bytes[i]);
        }

        resolve(btoa(binary));
      };

      reader.onerror = () => reject(reader.error || new Error('Unable to read certificate file'));
      reader.readAsArrayBuffer(file);
    });
  }

  private isValidBase64(value: string): boolean {
    const base64Regex = /^[A-Za-z0-9+/]+={0,2}$/;
    return !!value && base64Regex.test(value);
  }

  private escapeHtml(value: string): string {
    return (value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
